// src/services/auditService.js - HIPAA Audit Logging
import * as awsApi from './awsApiService';

/**
 * Log an audit event to the database via Lambda API
 * @param {string} action - The action being performed (e.g., 'login', 'view_patient', 'export_data')
 * @param {string} resourceType - Type of resource (e.g., 'patient', 'claim', 'user')
 * @param {string} resourceId - ID of the resource being accessed
 * @param {object} metadata - Additional metadata about the action
 */
export const logAudit = async (action, resourceType = null, resourceId = null, metadata = {}) => {
  try {
    await awsApi.createAuditLog({
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      metadata: {
        ...metadata,
        user_agent: navigator?.userAgent || null
      }
    });
  } catch (err) {
    // Don't throw - audit logging should never break the app
  }
};

/**
 * Log a successful login
 */
export const logLogin = async (metadata = {}) => {
  await logAudit('login', 'session', null, {
    ...metadata,
    event: 'user_login_success'
  });
};

/**
 * Log a logout event
 */
export const logLogout = async (metadata = {}) => {
  await logAudit('logout', 'session', null, {
    ...metadata,
    event: 'user_logout'
  });
};

/**
 * Log a failed login attempt
 */
export const logFailedLogin = async (email, reason = 'invalid_credentials') => {
  try {
    // For failed logins, we may not have a token, so this may fail silently
    await awsApi.createAuditLog({
      action: 'login_failed',
      resource_type: 'session',
      metadata: {
        email: email,
        reason: reason,
        event: 'user_login_failed',
        user_agent: navigator?.userAgent || null
      }
    });
  } catch (err) {
    // Failed login audit may fail if no auth token - that's ok
  }
};

/**
 * Log workflow execution for HIPAA compliance
 */
export const logWorkflowExecution = async (workflowName, patientId = null, result = {}) => {
  await logAudit('workflow_execution', 'workflow', patientId, {
    workflow_name: workflowName,
    result_summary: result.success ? 'success' : 'failed',
    ...result
  });
};

/**
 * Log data export for HIPAA compliance
 */
export const logDataExport = async (exportType, recordCount, metadata = {}) => {
  await logAudit('data_export', exportType, null, {
    record_count: recordCount,
    ...metadata
  });
};

/**
 * Log data import for HIPAA compliance
 */
export const logDataImport = async (importType, results, metadata = {}) => {
  await logAudit('data_import', importType, null, {
    successful: results.successful || 0,
    failed: results.failed || 0,
    duplicates: results.duplicates || 0,
    ...metadata
  });
};

/**
 * Log PHI access for HIPAA compliance
 */
export const logPHIAccess = async (resourceType, resourceId, accessType = 'view') => {
  await logAudit(`phi_${accessType}`, resourceType, resourceId, {
    access_type: accessType
  });
};
